
import { Component, Vue, Prop } from "vue-property-decorator";

import skills_routes from "@/api/routes/skills";
import { getState, setState, delState } from "@/helpers/local_storage";

import {
  EAktStatus,
  EAktType,
  aktTypes,
  aktStatuses
} from "@/models/akt/question_version";
import SkillCategory, { ESkillCategory } from "@/models/skill/category";
import SkillLocal from "@/models/skill/local";
import { IAktTag } from "@/models/akt/tag";
import { Debounce } from "@/helpers";

export interface IFilter {
  akt_type_id: EAktType | null;
  akt_status_id: EAktStatus | null;
  skill_category_id: ESkillCategory | null;
  skill_id: string | null;
  tag_ids: string[] | null;
}

@Component({
  name: "CAktQuestionFilter"
})
export default class CAktQuestionFilter extends Vue {
  public show_filter: boolean = false;
  public filters: Map<string, boolean> = new Map();
  public filters_count: number = 0;

  private filter_key_prefix = "akt_question_filters_";

  @Prop({ required: false, default: false })
  public save_filters!: boolean;

  @Prop({ required: true }) public tags!: IAktTag[];
  public tag_ids: string[] | null = null;
  private tag_ids_key = `${this.filter_key_prefix}_tag_ids`;

  @Prop({ required: false, default: false })
  public disabled_akt_type_id!: boolean;

  @Prop({ required: false, default: null })
  public akt_type_id!: EAktType | null;

  public akt_types = aktTypes;
  public selected_akt_type_id: EAktType | null = null;
  private selected_akt_type_id_key = `${this.filter_key_prefix}_selected_akt_type_id`;

  @Prop({ required: false, default: false })
  public disabled_akt_status_id!: boolean;

  @Prop({ required: false, default: null })
  public akt_status_id!: EAktStatus | null;

  public akt_statuses = aktStatuses;
  public selected_akt_status_id: EAktStatus | null = null;
  private selected_akt_status_id_key = `${this.filter_key_prefix}_selected_akt_status_id`;

  public skill_categories: SkillCategory[] = [];
  public skill_category_id: ESkillCategory | null = null;
  private skill_category_id_key = `${this.filter_key_prefix}_skill_category_id`;

  public skills: SkillLocal[] = [];
  public skill_id: string | null = null;
  private skill_id_key = `${this.filter_key_prefix}_skill_id`;

  protected async created() {
    await Promise.all([this.loadSkillCategories()]).finally(() => {
      if (this.save_filters) {
        this.getLocalStorageState();

        this.apply();
      } else {
        this.setDefaultAktFilters();

        this.filters_count = this.filters.size;
      }
    });
  }

  private setDefaultAktFilters() {
    this.selected_akt_type_id = this.akt_type_id;
    if (this.selected_akt_type_id) {
      this.filters.set("akt_type", true);
    }
    setState(this.selected_akt_type_id_key, this.selected_akt_type_id);

    this.selected_akt_status_id = this.akt_status_id;
    if (this.selected_akt_status_id) {
      this.filters.set("akt_status", true);
    }
    setState(this.selected_akt_status_id_key, this.selected_akt_status_id);
  }

  public clearFilters() {
    this.filters.clear();

    this.skill_category_id = null;
    delState(this.skill_category_id_key);

    this.skills = [];
    this.skill_id = null;
    delState(this.skill_id_key);

    this.tag_ids = [];
    delState(this.tag_ids_key);

    this.setDefaultAktFilters();

    this.filters_count = this.filters.size;

    this.apply();
  }

  public changeAktTypeFilter() {
    if (this.selected_akt_type_id) {
      this.filters.set("akt_type", true);
    } else {
      this.filters.delete("akt_type");
    }

    setState(this.selected_akt_type_id_key, this.selected_akt_type_id);

    this.filters_count = this.filters.size;

    this.apply();
  }

  public changeAktStatusFilter() {
    if (this.selected_akt_status_id) {
      this.filters.set("akt_status", true);
    } else {
      this.filters.delete("akt_status");
    }

    setState(this.selected_akt_status_id_key, this.selected_akt_status_id);

    this.filters_count = this.filters.size;

    this.apply();
  }

  public async changeSkillCategoryFilter() {
    this.skill_id = null;
    this.filters.delete("skill");
    delState(this.skill_id_key);

    if (this.skill_category_id) {
      this.filters.set("skill_category", true);

      await this.loadSkills();
    } else {
      this.filters.delete("skill_category");

      this.skills = [];
    }

    setState(this.skill_category_id_key, this.skill_category_id);

    this.filters_count = this.filters.size;

    this.apply();
  }

  public changeSkillFilter() {
    if (this.skill_id) {
      this.filters.set("skill", true);
    } else {
      this.filters.delete("skill");
    }

    setState(this.skill_id_key, this.skill_id);

    this.filters_count = this.filters.size;

    this.apply();
  }

  public changeTagFilter() {
    if (this.tag_ids?.length) {
      this.filters.set("tag", true);
    } else {
      this.filters.delete("tag");
    }

    setState(this.tag_ids_key, this.tag_ids);

    this.filters_count = this.filters.size;

    this.apply();
  }

  @Debounce(500)
  private apply() {
    this.$emit("apply", {
      akt_type_id: this.selected_akt_type_id,
      akt_status_id: this.selected_akt_status_id,
      skill_category_id: this.skill_category_id,
      skill_id: this.skill_id,
      tag_ids: this.tag_ids
    });
  }

  private async loadSkillCategories() {
    return this.$api
      .get(skills_routes.categories)
      .then(({ data: res }: { data: SkillCategory[] }) => {
        this.skill_categories = res;
      });
  }

  private async loadSkills() {
    this.skills = [];

    if (this.skill_category_id) {
      return this.$api
        .get(skills_routes.skills, {
          params: {
            paginate: false,
            includes: "all",
            category_id: this.skill_category_id
          }
        })
        .then(({ data: res }: { data: { skills: SkillLocal[] } }) => {
          this.skills = res.skills;
        });
    }
  }

  private getLocalStorageState() {
    const selected_akt_type_id = getState<EAktType | null>(
      this.selected_akt_type_id_key
    );
    if (selected_akt_type_id?.value) {
      this.selected_akt_type_id = selected_akt_type_id.value;

      if (this.selected_akt_type_id) {
        this.filters.set("akt_type", true);
      }
    }

    const selected_akt_status_id = getState<EAktStatus | null>(
      this.selected_akt_status_id_key
    );
    if (selected_akt_status_id?.value) {
      this.selected_akt_status_id = selected_akt_status_id.value;

      if (this.selected_akt_status_id) {
        this.filters.set("akt_status", true);
      }
    }

    const skill_category_id = getState<ESkillCategory | null>(
      this.skill_category_id_key
    );
    if (skill_category_id?.value) {
      this.skill_category_id = skill_category_id.value;

      if (this.skill_category_id) {
        this.filters.set("skill_category", true);
      }
    }

    const skill_id = getState<string | null>(this.skill_id_key);
    if (skill_id?.value) {
      this.skill_id = skill_id.value;

      if (this.skill_id) {
        this.filters.set("skill", true);
      }
    }

    const tag_ids = getState<string[] | null>(this.tag_ids_key);
    if (tag_ids?.value) {
      this.tag_ids = tag_ids.value;

      if (this.tag_ids?.length) {
        this.filters.set("tag", true);
      }
    }

    this.filters_count = this.filters.size;
  }
}
